import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiStar, FiHeart, FiGift, FiSettings, FiShield, FiPhone } = FiIcons;

const Services = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const services = [
    {
      icon: FiHeart,
      title: "Wedding Jewelry",
      description: "Exquisite bridal sets, engagement rings, and wedding bands crafted for your special day",
      category: "wedding"
    },
    {
      icon: FiStar,
      title: "Custom Design",
      description: "Personalized jewelry pieces designed according to your vision and preferences",
      category: "custom"
    },
    {
      icon: FiGift,
      title: "Gold Jewelry",
      description: "Premium gold necklaces, bracelets, earrings and chains in various karats",
      category: "gold"
    },
    {
      icon: FiShield,
      title: "Diamond Jewelry",
      description: "Certified diamond rings, pendants and earrings with authenticity guarantee",
      category: "diamond"
    },
    {
      icon: FiSettings,
      title: "Jewelry Repair",
      description: "Professional repair and restoration services for all types of jewelry",
      category: "repair"
    },
    {
      icon: FiStar,
      title: "Jewelry Cleaning",
      description: "Expert cleaning and polishing services to restore your jewelry's shine",
      category: "maintenance"
    }
  ];

  const categories = [
    { id: 'all', label: 'All Services' },
    { id: 'wedding', label: 'Wedding' },
    { id: 'custom', label: 'Custom Design' },
    { id: 'gold', label: 'Gold Jewelry' },
    { id: 'diamond', label: 'Diamond Jewelry' },
    { id: 'repair', label: 'Repair & Maintenance' }
  ];

  const filteredServices = selectedCategory === 'all' 
    ? services 
    : services.filter(service => service.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-yellow-400 to-yellow-500 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="text-4xl md:text-6xl font-bold font-serif mb-6">
              Our Services
            </h1>
            <p className="text-xl max-w-3xl mx-auto opacity-90">
              From custom designs to expert repairs, we offer comprehensive jewelry services 
              to meet all your precious metal and gemstone needs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Filter */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-200 ${
                  selectedCategory === category.id
                    ? 'bg-yellow-400 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-yellow-100'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow duration-200"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center mb-6">
                  <SafeIcon icon={service.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <a
                  href="tel:+971508087073"
                  className="text-yellow-600 font-medium hover:text-yellow-700 transition-colors duration-200 flex items-center space-x-2"
                >
                  <SafeIcon icon={FiPhone} className="w-4 h-4" />
                  <span>Call for Details</span>
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section Removed as requested */}

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <h2 className="text-4xl font-bold text-gray-800 font-serif">
              Ready to Create Your <span className="text-yellow-600">Dream Jewelry</span>?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Contact us today to discuss your jewelry needs. Our expert team is ready to bring your vision to life.
            </p>
            <a
              href="tel:+971508087073"
              className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white px-8 py-4 rounded-lg font-semibold hover:from-yellow-500 hover:to-yellow-600 transition-all duration-200 inline-flex items-center space-x-2 shadow-lg"
            >
              <SafeIcon icon={FiPhone} className="w-5 h-5" />
              <span>Call Now: +971 50 808 7073</span>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;